// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Reservation card (list item)
// ---------------------------------------------------------------------------

import type { Reservation } from '@/lib/types';

interface ReservationCardProps {
  reservation: Reservation;
  onClick: () => void;
}

const STATUS_BADGE: Record<string, string> = {
  confirmed: 'bg-success/20 text-success',
  pending: 'bg-warning/20 text-warning',
  cancelled: 'bg-danger/20 text-danger',
  completed: 'bg-gray-500/20 text-gray-400',
  no_show: 'bg-danger/20 text-danger',
};

const STATUS_LABELS: Record<string, string> = {
  confirmed: 'Confirmee',
  pending: 'En attente',
  cancelled: 'Annulee',
  completed: 'Terminee',
  no_show: 'No-show',
};

/**
 * Format a reservation date + time into a user-friendly string.
 * Shows "Aujourd'hui HH:MM" if the reservation is today, otherwise "DD/MM HH:MM".
 */
function formatDateTime(date?: string, time?: string): string {
  if (!date) return 'Non defini';

  const dateObj = new Date(`${date}T${time || '00:00:00'}`);
  const today = new Date();

  if (dateObj.toDateString() === today.toDateString()) {
    return `Aujourd'hui ${dateObj.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
  }

  return dateObj.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function ReservationCard({ reservation, onClick }: ReservationCardProps) {
  const badge = STATUS_BADGE[reservation.status] ?? 'bg-gray-500/20 text-gray-400';
  const label = STATUS_LABELS[reservation.status] ?? reservation.status;

  return (
    <button
      type="button"
      onClick={onClick}
      className="card mx-4 mb-3 w-auto text-left animate-fade-in active:scale-[0.98] transition-transform"
    >
      {/* Header: ID + status */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-accent font-semibold">#{reservation.id}</span>
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${badge}`}>
          {label}
        </span>
      </div>

      {/* Body */}
      <div className="space-y-1.5 text-sm">
        {/* Client name */}
        <div className="flex items-center gap-2 text-white">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0 text-gray-400">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          <span className="font-medium">{reservation.client_name || 'Client'}</span>
        </div>

        {/* Phone */}
        {reservation.client_phone && (
          <div className="flex items-center gap-2 text-gray-400">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
            <span>{reservation.client_phone}</span>
          </div>
        )}

        {/* Table + Time + Party size row */}
        <div className="flex items-center gap-4 text-gray-400">
          {/* Table */}
          <div className="flex items-center gap-1">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            </svg>
            <span className="text-xs">{reservation.table_id || 'N/A'}</span>
          </div>

          {/* Time */}
          <div className="flex items-center gap-1">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span className="text-xs">{formatDateTime(reservation.date, reservation.time)}</span>
          </div>

          {/* Party size */}
          <div className="flex items-center gap-1">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <span className="text-xs">{reservation.party_size}p</span>
          </div>
        </div>
      </div>
    </button>
  );
}
